import { InsecureServerAuth, KeyPair, Listeners, LocalKeyPair, RemoteKey, ThingPeer } from 'thingrtc-peer';
import { Connection } from './connections';
import { createSshConnection, SSHConnection } from './ssh';

const SIGNALLING_SERVER_URL = 'wss://signalling.thingify.app/signalling';
const REMOTE_HOST = '10.0.1.1';

export interface PeerEventListeners {
    onConnecting(): void;
    onPeerConnected(): void;
    onShellConnected(): void;
    onWriteToTerminal(buf: Uint8Array): void;
    onClose(reason: string): void;
}

export async function loadLocalKeyPair(): Promise<LocalKeyPair> {
    const loaded = await LocalKeyPair.loadLocalKeyPair();
    if (loaded) {
        return loaded;
    } else {
        const keyPair = await LocalKeyPair.createLocalKeyPair();
        await keyPair.save();
        return keyPair;
    }
}

export interface PeerConnection {
    insertData(data: string): void;
    resize(rows: number, cols: number): void;
    disconnect(): void;
}

export async function connect(connection: Connection, peerEventListeners: PeerEventListeners, rows: number, cols: number): Promise<PeerConnection> {
    console.log('Connecting...');
    const remoteKey = await RemoteKey.createRemoteKey(connection.publicKey);
    const peerConfig = await KeyPair.createConfig(remoteKey, await loadLocalKeyPair(), 'responder');
    const serverAuth = new InsecureServerAuth(peerConfig.pairingId, peerConfig.role);

    let sshConnection: SSHConnection|null = null;

    const listeners: Listeners = {
        connectionStateListener: async state => {
            console.log(`Peer connection state: ${state}`);
            if (state === 'connected') {
                sshConnection = await sshConnect(peer, peerEventListeners.onWriteToTerminal, () => peerEventListeners.onShellConnected(), () => peerEventListeners.onClose('Session closed'), connection.username, connection.password, rows, cols);
                peerEventListeners.onPeerConnected();
            } else if (state === 'connecting') {
                peerEventListeners.onConnecting();
            }
        },
        errorListener: error => {
            console.error(error);
            peerEventListeners.onClose(`Peer error: ${error}`);
        }
    };

    const peer = new ThingPeer(SIGNALLING_SERVER_URL, serverAuth, peerConfig, listeners);
    peer.connect([]);

    return {
        insertData: data => sshConnection?.readFromTerminal(data),
        resize: (rows, cols) => sshConnection?.resize(rows, cols),
        disconnect: () => peer.disconnect(),
    };
}

async function sshConnect(peer: ThingPeer, onWriteToTerminal: (buf: Uint8Array) => void, onOpen: () => void, onClose: () => void, username: string, password: string, rows: number, cols: number): Promise<SSHConnection> {
    const sshConnection = await createSshConnection(
        buf => onWriteToTerminal(buf),
        buf => dc.sendMessage(buf.buffer as ArrayBuffer),
        onOpen,
        () => {
            // When the session closes, first close the entire peer connection
            // so that we don't have events retrying the connection in the
            // background.
            peer.disconnect();
            onClose();
        }
    );

    const dc = await peer.createDataChannel(`tcp:${REMOTE_HOST}:22`, true);

    dc.on('binaryMessage', message => {
        console.log('Binary message received (JS)');
        sshConnection.readFromNetwork(new Uint8Array(message));
    });

    sshConnection.connect(`${REMOTE_HOST}:22`, username, password, rows, cols);

    return sshConnection;
}
